//General ThreeJS Utils

import {Box3, BoxGeometry, BoxHelper, Group, Line3, LineSegments, Mesh, SphereGeometry, Vector2, Vector3} from "three";
import * as Globals from "../globals";
import {CSG} from "three-csg-ts";
import {intersectPlane, raycaster,scene} from "../sceneManager";
import {buildingGroup, mousePosition} from "../main";
import {EdgesGeometry} from "../override/EdgesGeometry";
import {toFixedFloat} from "./utils";
import {lineMaterial, previewMaterial, previewNotAllowedMaterial} from "../constants/materialConstants";

var center = null;
//Create Elements
export function addBoundingBox(mesh, addTo = null) {
    let bBox = new BoxHelper(mesh, Globals.boundingBoxColor);
    bBox.layers.set(Globals.layers.default);
    if (addTo !== null) {
        addTo.addWithUuid(bBox);
    }
    return bBox;
}

export function addEdgeGeometry(mesh) {
    let edges = new EdgesGeometry(mesh.geometry);
    let line = new LineSegments(edges, lineMaterial);
    line.renderOrder = 1;
    mesh.addWithUuid(line);
}

export function addPointsToScene(points, material = previewMaterial) {
    for (let i = 0; i < points.length; i++) {
        if (i % 2 === 0) {
            addPointToScene(points[i], material);
        } else {
            addPointToScene(points[i], previewNotAllowedMaterial);
        }
    }
}

export function addPointToScene(point, material = previewMaterial) {
    let geometry = new SphereGeometry(0.025, 10, 10);
    let sphere = new Mesh(geometry, material);
    sphere.position.copy(point);
    buildingGroup.addWithUuid(sphere);
}

export function createCube(width, depth, height, position, group, material = null) {
    const geometry = new BoxGeometry(width, height, depth);
    if (material === null) {
        material = previewMaterial;
    }
    const cube = new Mesh(geometry, material);
    setObjectPosition(cube, position);
    group.addWithUuid(cube);
    return cube;
}

export function createGroup(name, addTo = null) {
    let group = new Group();

    group.name = name;

    if (addTo !== null) {
        addTo.addWithUuid(group);
    }

    return group;
}

//Modify elements
export function cutMeshToSize(mesh, axis, length) {
    let meshBBox = mesh.geometry.boundingBox;
    let currentLength = getLengthByAxis(meshBBox, axis);
    let cutToolLength = Math.ceil(currentLength) - length;
    let cutToolPosition;

    let box;
    if (axis === "x") {
        cutToolPosition = meshBBox.min.x + length + (cutToolLength / 2);
        box = new Mesh(new BoxGeometry(cutToolLength, Math.ceil(getLengthByAxis(meshBBox, "y")), Math.ceil(getLengthByAxis(meshBBox, "z"))), lineMaterial);
        box.position.set(cutToolPosition, 0, 0);
    } else if (axis === "-x") {
        cutToolPosition = meshBBox.max.x - length - (cutToolLength / 2);
        box = new Mesh(new BoxGeometry(cutToolLength, Math.ceil(getLengthByAxis(meshBBox, "y")), Math.ceil(getLengthByAxis(meshBBox, "z"))), lineMaterial);
        box.position.set(cutToolPosition, 0, 0);
    } else if (axis === "y") {
        cutToolPosition = meshBBox.min.y + length + (cutToolLength / 2);
        box = new Mesh(new BoxGeometry(Math.ceil(getLengthByAxis(meshBBox, "x")), cutToolLength, Math.ceil(getLengthByAxis(meshBBox, "z"))), lineMaterial);
        box.position.set(0, cutToolPosition, 0);
    } else if (axis === "z") {
        cutToolPosition = meshBBox.min.z + length + (cutToolLength / 2);
        box = new Mesh(new BoxGeometry(Math.ceil(getLengthByAxis(meshBBox, "x")), Math.ceil(getLengthByAxis(meshBBox, "y")), cutToolLength), lineMaterial);
        box.position.set(0, 0, cutToolPosition);
    }

    box.name = "cutMesh";
    box.updateMatrix();
    mesh.updateMatrix();
    mesh = CSG.subtract(mesh, box);
    mesh.geometry.center();
    mesh.geometry.computeBoundingBox();
    return mesh;
}

export function setObjectPosition(object, position) {
    object.position.set(position.x, position.y, position.z);
}

//Find objects
export function findElementByUUID(searchObject, uuid) {
    let newChild = null;
    try {
        searchObject.children.forEach(function (child) {
            if (child.userData.uuid === uuid) {
                newChild = child;
            }
        });
    } catch (e) {
        console.error("Error: Couldn't find element by UUID");
    }

    return newChild;
}

export function findChildByName(searchObject, name, includes = false) {
    let foundChild = null;
    name = name.toLowerCase();
    for (let i = 0; i < searchObject.children.length; i++) {
        let child = searchObject.children[i];

        if (!includes && child.name.toLowerCase() === name) {
            foundChild = child;
            break;
        } else if (includes && child.name.toLowerCase().includes(name)) {
            foundChild = child;
            break;
        }
    }

    return foundChild;
}

export function findChildrenByName(searchObject, name, includes = false, recursive = false, recursionDepth = -1) {
    if (searchObject === null || searchObject === undefined) {
        return null;
    }

    let children = [];
    name = name.toLowerCase();

    for (let i = 0; i < searchObject.children.length; i++) {
        let child = searchObject.children[i];
        if (includes) {
            if (child.name.toLowerCase().includes(name)) {
                children.push(child);
            }
        } else {
            if (child.name.toLowerCase() === name) {
                children.push(child);
            }
        }

        if (recursive) {
            let depth = recursionDepth === -1 ? -1 : recursionDepth - 1;
            if (depth === -1 || depth > 0) {
                children = children.concat(findChildrenByName(child, name, includes, recursive, depth));
            }
        }
    }

    return children;
}

export function findChildrenByNames(searchObject, names, recursive = false) {
    let children = [];

    for (let i = 0; i < searchObject.children.length; i++) {
        let child = searchObject.children[i];
        if (names.includes(child.name)) {
            children.push(child);
        }

        if (recursive && child.children?.length) {
            children = children.concat(findChildrenByNames(child, names, recursive));
        }
    }

    return children;
}

export function findElementsByUserData(searchObject, key, value) {
    let foundElements = [];

    if (searchObject.children === undefined) {
        return foundElements;
    }
    for (let i = 0; i < searchObject.children.length; i++) {
        let child = searchObject.children[i];
        if (child.userData[key] === value) {
            foundElements.push(child);
        }

        if (child.children.length > 0) {
            foundElements = foundElements.concat(findElementsByUserData(key, value, child));
        }
    }

    return foundElements;
}

export function getIntersectedObjectByName(intersectedObjects, name, nameIncludes = false) {
    let objects = [];
    if (nameIncludes) {
        name = name.toLowerCase();
    }

    if (nameIncludes) {
        for (let i = 0; i < intersectedObjects.length; i++) {
            let object = intersectedObjects[i];
            if (object.name.toLowerCase().includes(name)) {
                objects.push(object);
            }
        }
    } else {
        for (let i = 0; i < intersectedObjects.length; i++) {
            let object = intersectedObjects[i];
            if (object.name === name) {
                objects.push(object);
            }
        }
    }

    return objects;
}

export function getIntersectedObjectByType(intersectedObjects, type) {
    let objects = [];
    for (let i = 0; i < intersectedObjects.length; i++) {
        let object = intersectedObjects[i];
        if (object.type === type) {
            objects.push(object);
        }
    }

    return objects;
}

export function getHighestParentGroup(highestParent, object, depth = 1) {
    if (object == null) {
        return object;
    }
    //Get parent object that's directly under highest parent
    let parents = [];
    while (object.parent !== null && object.parent !== undefined && object.parent !== highestParent) {
        object = object.parent;
        parents.push(object);
    }

    if (depth !== 1) {
        object = parents[parents.length - depth];
    }

    return object;
}

export function getDifferenceInScreenCoordinates(camera, v1, v2) {
    const point1Screen = v1.clone().project(camera);
    const point2Screen = v2.clone().project(camera);

    const dx = Math.abs(point1Screen.x - point2Screen.x);
    const dy = Math.abs(point1Screen.y - point2Screen.y);
    const dz = Math.abs(point1Screen.z - point2Screen.z);
    return new Vector3(dx, dy, dz);
}

//Mouse
export function getMouseCoordinates(e, container) {
    let size = container.getBoundingClientRect();
    let mouse = {x: 0, y: 0};
    mouse.x = (e.clientX - size.left) / size.width * 2 - 1;
    mouse.y = -((e.clientY - size.top) / size.height) * 2 + 1;
    return mouse;
}

export function getMousePosition() {
    let intersect = raycaster.intersectObject(intersectPlane);
    if (intersect.length > 0) {
        return intersect[0].point;//Mouse position in world coordinates
    }
    return undefined;
}

export function getScreenPositionFromVector(container, camera, vector) {
    let widthHalf = 0.5 * container.getBoundingClientRect().width;
    let heightHalf = 0.5 * container.getBoundingClientRect().height;

    vector.project(camera);
    vector.x = (vector.x * widthHalf) + widthHalf;
    vector.y = -(vector.y * heightHalf) + heightHalf;
    vector.z = 0;

    return vector;
}

//Shapes and meshes from Points
export function convertArrayToVector(point) {
    return new Vector3(point[0], 0, point[1]);
}

export function convertArrayOfArraysToVector(points) {
    let vectors = [];
    for (let i = 0; i < points.length; i++) {
        vectors.push(convertArrayToVector(points[i]));
    }

    return vectors;
}

//Bounds
export const boundsPoints = {
    UpperLeft: 1, UpperRight: 2, LowerRight: 3, LowerLeft: 4, NotOnBounds: 5
};

export function getBoundsPositionFromPoint(bounds, point) {
    if (bounds.min.x === point.x && bounds.min.z === point.z) {
        return boundsPoints.LowerLeft;
    } else if (bounds.max.x === point.x && bounds.max.z === point.z) {
        return boundsPoints.UpperRight;
    } else if (bounds.min.x === point.x && bounds.max.z === point.z) {
        return boundsPoints.UpperLeft;
    } else if (bounds.min.z === point.z && bounds.max.x === point.x) {
        return boundsPoints.LowerRight;
    }

    return boundsPoints.NotOnBounds;
}

export function getBoundingBoxLines(meshes, singleHeight = true) {
    //TODO Für nicht singleHeight
    let boundLines = [];
    for (let i = 0; i < meshes.length; i++) {
        let mesh = meshes[i];
        let meshBounds = new Box3().setFromObject(mesh);
        let points = getPointsFromBoundingBox(meshBounds, singleHeight);
        for (let j = 0; j < points.length; j++) {
            points[j].y = 0;
        }

        let newobject = {object: mesh, lines: []};
        for (let j = 0; j < points.length; j++) {
            let line;
            if (j === points.length - 1) {
                line = new Line3(points[j], points[0]);
            } else {
                line = new Line3(points[j], points[j + 1]);
            }
            newobject.lines.push(line);
        }

        boundLines.push(newobject);
    }

    return boundLines;
}
/*Get the 4 boundary lines that define a drawn element in 2d xz plane (wall line segment for example), triggered on 2nd click*/
export function getBoundingBoxLinesByAxis(meshes, singleHeight = true) {
    //TODO Für nicht singleHeight
    let boundLines = {x: [], z: []};
    for (let i = 0; i < meshes.length; i++) {
        let mesh = meshes[i];
        let meshBounds = new Box3().setFromObject(mesh);
        //Gets the 8 vertice points of a rectangular parallelepiped whose faces are each perpendicular to one of the basis vectors (x,y,z)
        let points = getPointsFromBoundingBox(meshBounds, singleHeight);
        for (let j = 0; j < points.length; j++) {
            points[j].y = 0;
        }

        points = copyVectorArray(points, true);

        for (let j = 0; j < points.length; j++) {
            let line;
            if (j === points.length - 1) {
                line = new Line3(points[j], points[0]);
            } else {
                line = new Line3(points[j], points[j + 1]);
            }

            const meshName = mesh && mesh.name ? mesh.name : "";
            if (meshName.indexOf("raehm") !== -1) {
                line.snapKind = "wall";
            } else if (meshName.indexOf("pillar") !== -1) {
                line.snapKind = "pillar";
            } else if (meshName.toLowerCase().indexOf("beam") !== -1) {
                line.snapKind = "beam";
            } else {
                line.snapKind = "other";
            }

            if (line.start.x === line.end.x) {
                boundLines.z.push(line);
            } else {
                boundLines.x.push(line);
            }
        }
    }

    return boundLines;
}

export function getClosestBoundaryLine(elements, position = null) {
    position = position || mousePosition;
    if (elements.length <= 0) {
        return {element: null, line: null, distance: null};
    }
    let boundLines = getBoundingBoxLines(elements, true);
    let closestElement = null;
    let closestLine = null;
    let closestDistance = null;
    let closestPoint = null;
    for (let i = 0; i < boundLines.length; i++) {
        for (let j = 0; j < boundLines[i].lines.length; j++) {
            let v = boundLines[i].lines[j].closestPointToPoint(position, true, new Vector3());
            let distance = v.distanceTo(position);
            if (closestDistance === null || distance < closestDistance) {
                closestDistance = distance;
                closestLine = boundLines[i].lines[j];
                closestElement = boundLines[i].object;
                closestPoint = v;
            }
        }
    }

    return {element: closestElement, line: closestLine, distance: closestDistance, point:closestPoint};
}

export function getCenterOfPoints(points, yOffset = null) {
    let box = (new Box3()).setFromPoints(points);
    let center = new Vector3();
    box.getCenter(center);
    return center;
}

export function sortPointsClockwise(points)
{
    if (!center) {
        center = getCenterOfPoints(points);
    }
    points.sort(comparePointsOrder);
    center = null;

    return points;
}

export function sortObjClockwise(points, objects)
{
    if (!center) {
        center = getCenterOfPoints(points);
    }
    objects.sort(compareObjOrder);
    center = null;

    return objects;
}

function compareObjOrder(el1, el2)
{
    let a = el1.snapPoint;
    let b = el2.snapPoint;
    // compute the cross product of vectors (center -> a) x (center -> b)
    let det = (a.z * b.x) - (b.z * a.x)
    if (det < 0) {
        return 1;
    }
    if (det > 0) {
        return -1;
    }

    return 0;
}

function comparePointsOrder(a, b)
{
    if (a.x - center.x >= 0 && b.x - center.x < 0) {
        return 1;
    }
    if (a.x - center.x < 0 && b.x - center.x >= 0) {
        return -1;
    }
    if (a.x - center.x == 0 && b.x - center.x == 0) {
        if (a.z - center.z >= 0 || b.z - center.z >= 0)
            return a.z > b.z;
        return b.z > a.z;
    }

    // compute the cross product of vectors (center -> a) x (center -> b)
    let det = (a.x - center.x) * (b.z - center.z) - (b.x - center.x) * (a.z - center.z);
    if (det < 0) {
        return 1;
    }
    if (det > 0) {
        return -1;
    }

    return 0;

    // points a and b are on the same line from the center
    // check which point is closer to the center
    let d1 = (a.x - center.x) * (a.x - center.x) + (a.z - center.z) * (a.z - center.z);
    let d2 = (b.x - center.x) * (b.x - center.x) + (b.z - center.z) * (b.z - center.z);
    return d1 > d2 ? 1 : -1;
}

export function getPointsFromBoundingBox(bounds, singleHeight = false) {
    let points = [];
    //Reihenfolge ist wichtig um ggf einen Path aus diesen zu erstellen
    if (!singleHeight) {
        points.push(bounds.min);
        points.push(new Vector3(bounds.min.x, bounds.min.y, bounds.max.z));
        points.push(new Vector3(bounds.max.x, bounds.min.y, bounds.max.z));
        points.push(new Vector3(bounds.max.x, bounds.min.y, bounds.min.z));
    }
    points.push(bounds.max);
    points.push(new Vector3(bounds.max.x, bounds.max.y, bounds.min.z));
    points.push(new Vector3(bounds.min.x, bounds.max.y, bounds.min.z));
    points.push(new Vector3(bounds.min.x, bounds.max.y, bounds.max.z));

    return points;
}

export function getPointsFromBoundingBoxClockwise(bounds) {
    let points = [];
    points.push(new Vector3(bounds.min.x, bounds.max.y, bounds.min.z));
    points.push(new Vector3(bounds.max.x, bounds.max.y, bounds.min.z));
    points.push(bounds.max);
    points.push(new Vector3(bounds.min.x, bounds.max.y, bounds.max.z));

    return points;
}

export function getPointsFromBoundingBox2D(bounds) {
    let points = [];
    //Reihenfolge ist wichtig um ggf einen Path aus diesen zu erstellen
    points.push(new Vector2(bounds.max.x, bounds.max.z));
    points.push(new Vector2(bounds.max.x, bounds.min.z));
    points.push(new Vector2(bounds.min.x, bounds.min.z));
    points.push(new Vector2(bounds.min.x, bounds.max.z));
    return points;
}

//Other
export function isParent(parent, object) {
    if (!object.parent) {
        return false;
    }

    if (object.parent === parent) {
        return true;
    }

    return isParent(parent, object.parent);
}

export function getLengthByAxis(boundingBox, axis) {
    let length = -1;

    if (axis === "x") {
        length = boundingBox.max.x - boundingBox.min.x;
    } else if (axis === "y") {
        length = boundingBox.max.y - boundingBox.min.y;
    } else if (axis === "z") {
        length = boundingBox.max.z - boundingBox.min.z;
    }

    return length;
}

export function copyVectorArray(arr, round = false) {
    //Kopieren eines Array ohne Referenz auf das Ursprungsarray
    let newArr = [];

    if (!round) {
        for (let i = 0; i < arr.length; i++) {
            newArr.push(new Vector3().copy(arr[i]));
        }
    } else {
        for (let i = 0; i < arr.length; i++) {
            newArr.push(new Vector3(toFixedFloat(arr[i].x), toFixedFloat(arr[i].y), toFixedFloat(arr[i].z)));
        }
    }

    return newArr;
}

export function setBox3FromObjectArray(objects) {
    let box3 = new Box3();
    for (let i = 0; i < objects.length; i++) {
        box3.expandByObject(objects[i]);
    }

    return box3;
}

export function setMaterial(object, material, recursive = false) {
    object.material = material;
    for (let i = 0; i < object.children.length; i++) {
        setMaterial(object.children[i], material, recursive);
    }
}
